import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, rgba(25, 30, 50, 0.98) 0%, rgba(20, 25, 45, 0.98) 100%);
  border-radius: 16px;
  width: 500px;
  max-width: 90%;
  padding: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  color: #e8eaed;
  border: 1px solid rgba(99, 102, 241, 0.2);
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 24px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 2px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
    transform: rotate(90deg);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  
  input[type="checkbox"] {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(99, 102, 241, 0.3);
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.3);
    cursor: pointer;
    margin-right: 10px;
    position: relative;
    transition: all 0.2s ease;
    
    &:checked {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      border-color: #6366f1;
    }
    
    &:checked::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
    
    &:hover {
      border-color: rgba(99, 102, 241, 0.5);
    }
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  font-size: 14px;
  color: #fff;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  font-size: 14px;
  color: #fff;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.6;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: 1px solid transparent;
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5558e3 0%, #7c3aed 100%);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const IconSelectorContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-top: 12px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  max-height: 320px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.7);
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: ${props => props.selected 
    ? 'rgba(99, 102, 241, 0.2)' 
    : 'rgba(0, 0, 0, 0.2)'};
  border: 2px solid ${props => props.selected ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  cursor: pointer;
  padding: 10px;
  transition: all 0.2s ease;
  width: 100%;
  height: 50px;
  position: relative;

  &:hover {
    background: ${props => props.selected 
      ? 'rgba(99, 102, 241, 0.3)' 
      : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${props => props.selected ? 'rgba(99, 102, 241, 0.8)' : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  /* Style the custom icon components inside */
  & > div {
    transform: scale(1.3);
  }
`;

// No emoji icons - we only use custom Feather Icons
const commonIcons = [];

/**
 * Modal component for creating or editing macros
 */
const MacroModal = ({ isOpen, onClose, onSave, initialValues, iconComponentsMap = {} }) => {
  const [formValues, setFormValues] = useState({
    name: '',
    prompt: '',
    icon: '📝', // Default icon
    includeConversation: true,
    iconType: null
  });
  
  const isEditing = !!initialValues?.id;
  
  useEffect(() => {
    if (initialValues) {
      setFormValues({
        name: initialValues.name || '',
        prompt: initialValues.prompt || '',
        icon: initialValues.icon || '📝',
        includeConversation: initialValues.includeConversation !== false,
        iconType: initialValues.iconType || null
      });
    } else {
      // Reset form when opening for a new macro
      setFormValues({
        name: '',
        prompt: '',
        icon: '📝',
        includeConversation: true,
        iconType: null
      });
    }
  }, [initialValues, isOpen]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleIconSelect = (icon) => {
    setFormValues(prev => ({
      ...prev,
      icon,
      iconType: null
    }));
  };
  
  const handleCustomIconSelect = (iconType) => {
    setFormValues(prev => ({
      ...prev,
      icon: null,
      iconType
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formValues,
      id: initialValues?.id
    });
  };
  
  // Create an array of all icon components with their types for the selector
  const customIconOptions = Object.entries(iconComponentsMap).map(([type, Component]) => ({
    type,
    component: <Component key={type} />
  }));
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{isEditing ? 'Edit Macro' : 'Create New Macro'}</ModalTitle>
          <CloseButton onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Macro Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formValues.name}
              onChange={handleChange}
              placeholder="Enter a name for your macro"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Choose Macro Icon</Label>
            
            {/* Combined Icons Section - Custom Icons and Emojis */}
            <IconSelectorContainer>
              {/* Custom Icons */}
              {customIconOptions.map((iconOption) => (
                <IconButton
                  key={`custom-${iconOption.type}`}
                  type="button"
                  selected={formValues.iconType === iconOption.type}
                  onClick={() => handleCustomIconSelect(iconOption.type)}
                >
                  {iconOption.component}
                </IconButton>
              ))}
              
              {/* Emoji Icons */}
              {commonIcons.map((icon, index) => (
                <IconButton
                  key={`emoji-${index}`}
                  type="button"
                  selected={formValues.icon === icon}
                  onClick={() => handleIconSelect(icon)}
                >
                  {icon}
                </IconButton>
              ))}
            </IconSelectorContainer>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="prompt">Macro Directive</Label>
            <Textarea
              id="prompt"
              name="prompt"
              value={formValues.prompt}
              onChange={handleChange}
              placeholder="Enter your directive here. This will be used when the macro is executed."
              required
            />
          </FormGroup>

          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="includeConversation"
                checked={formValues.includeConversation}
                onChange={handleChange}
              />
              <span>Include complete conversation when executing</span>
            </CheckboxLabel>
          </FormGroup>
          
          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>
              Cancel
            </CancelButton>
            <SaveButton 
              type="submit" 
              disabled={!formValues.name || !formValues.prompt || (!formValues.icon && formValues.iconType === null)}
            >
              {isEditing ? 'Update' : 'Create'}
            </SaveButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

MacroModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    prompt: PropTypes.string,
    icon: PropTypes.string,
    includeConversation: PropTypes.bool,
    iconType: PropTypes.string
  }),
  iconComponentsMap: PropTypes.object
};

export default MacroModal; 